import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';
import { Button, Grid, Container, Typography } from '@mui/material';
import TheatersIcon from '@mui/icons-material/Theaters';
import RestUtilities from '../services/RestUtilities';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  artistState,
  loadingState,
  toastState,
  videosState,
} from '../state/RecoilState';
import { Video } from '../@types/api';
import VideoCard from '../components/VideoCard';
import MustPickArtist from '../components/Common/MustPickArtist';
import ModalFileManager from '../components/Common/ModalFileManager';
import { uploadType } from '../Constants';

const sortVideos = (a: Video, b: Video) => {
  if (a.isLive < b.isLive) {
    return 1;
  } else if (a.isLive > b.isLive) {
    return -1;
  } else if (a.orderIndex > b.orderIndex) {
    return 1;
  } else if (a.orderIndex < b.orderIndex) {
    return -1;
  }
  return 0;
};

const Videos = () => {
  const artist = useRecoilValue(artistState);
  const setLoading = useSetRecoilState(loadingState);
  const setToast = useSetRecoilState(toastState);
  const [videos, setVideos] = useRecoilState<Video[] | undefined>(videosState);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [show, setShow] = useState<boolean>(false);
  const [fileType, setFileType] = useState<number>(uploadType.video);

  useEffect(() => {
    const fetchVideos = async (artistId: any) => {
      setLoading(true);
      const url = `api/Video/${artistId}`;
      const response = await RestUtilities.get(url);
      setLoading(false);
      if (response.ok) {
        setToast('Videos loaded');
        setVideos(response.content);
      }
    };
    if (artist && !videos) {
      fetchVideos(artist.artistId);
    }
  }, [artist, videos, setLoading, setVideos, setToast]);

  const onSaveVideo = async (video: Video) => {
    await RestUtilities.put(
      `api/Video/${video.videoId}/${artist?.artistId}`,
      video
    );
    setToast('Video saved');
    if (videos) {
      const newVideos = videos.filter((x) => x.videoId !== video.videoId);
      newVideos.push(video);
      setVideos(newVideos);
    }
  };

  const onRemoveVideo = async (video: Video) => {
    const url = `api/Video/${video.videoId}/${artist?.artistId}`;
    var response = await RestUtilities.delete(url);
    if (response.ok && videos) {
      setToast('Video removed');
      const newVideos = videos.filter((x) => x.videoId !== video.videoId);
      setVideos(newVideos);
    }
  };

  const renderVideos = (liveLounge: boolean, isLiveReel: boolean) => {
    if (!videos) return null;
    const sortedVideos = videos
      .filter(
        (x: Video) =>
          x.isAccoustic === liveLounge && x.isLiveReel === isLiveReel
      )
      .sort(sortVideos);
    return sortedVideos.map((x: Video) => {
      return (
        <Grid
          key={x.videoId}
          item
          lg={3}
          md={4}
          sm={6}
          onClick={(e) => onItemSelect(e, x)}
        >
          <VideoCard
            video={x}
            isSelected={x.videoId === selectedId}
            onSaveVideo={onSaveVideo}
            onRemoveVideo={onRemoveVideo}
          />
        </Grid>
      );
    });
  };

  const onItemSelect = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: Video
  ) => {
    if (selectedId !== item.videoId) {
      e.preventDefault();
      setSelectedId(item.videoId);
    }
  };

  const handleUploadSelect = async (item: any) => {
    if (!videos) {
      setShow(false);
      return;
    } else if (fileType === uploadType.poster) {
      const video = videos?.find((x) => x.videoUploadId === selectedId);
      if (!video) {
        setShow(false);
        return;
      }
      video.imageUpload = item;
      video.imageUploadId = item.uploadId;
      setVideos([...videos]);
      await RestUtilities.put(
        `api/Video/${video.videoId}/${artist?.artistId}`,
        video
      );
      setShow(false);
    } else {
      const video = {
        artistId: artist?.artistId,
        orderIndex: 99,
        isAccoustic: fileType === uploadType.liveLoungeVideo,
        isLiveReel: fileType === uploadType.liveReelVideo,
        videoUploadId: item.uploadId,
      } as Video;
      const response = await RestUtilities.post(
        `api/Video/${artist?.artistId}`,
        video
      );
      if (response.ok) {
        const newVideos = videos
          ? [...videos, response.content]
          : [response.content];
        setVideos(newVideos);
      }
      setShow(false);
    }
  };

  if (!artist) {
    return <MustPickArtist />;
  }

  return (
    <Container maxWidth='lg'>
      <Typography variant='h4' gutterBottom>
        {artist.artistName} Videos
      </Typography>
      <Typography variant='body2'>
        This is the current videos appearing on your band&#39;s website. You can
        reorder and hide videos. You can edit video filenames and song names in
        the <Link to={'/files'}>File Editor</Link>.
      </Typography>

      {videos && (
        <>
          <Grid container spacing={2} className='videos-row'>
            <Grid item xs={12}>
              <h2>Film Shoots</h2>
              <Button
                variant='contained'
                color='primary'
                endIcon={<TheatersIcon />}
                size='small'
                onClick={() => {
                  setFileType(uploadType.video);
                  setShow(true);
                }}
              >
                Add new film shoot video
              </Button>
            </Grid>
            {renderVideos(false, false)}
          </Grid>
          <Grid container spacing={2} className='videos-row'>
            <Grid item xs={12}>
              <h2>Live Lounge</h2>
              <Button
                variant='contained'
                color='primary'
                endIcon={<TheatersIcon />}
                size='small'
                onClick={() => {
                  setFileType(uploadType.liveLoungeVideo);
                  setShow(true);
                }}
              >
                Add new live-lounge video
              </Button>
            </Grid>
            {renderVideos(true, false)}
          </Grid>
          <Grid container spacing={2} className='videos-row' mb={3}>
            <Grid item xs={12}>
              <h2>Live Reels</h2>
              <Button
                variant='contained'
                color='primary'
                endIcon={<TheatersIcon />}
                size='small'
                onClick={() => {
                  setFileType(uploadType.liveReelVideo);
                  setShow(true);
                }}
              >
                Add new Live Reel
              </Button>
            </Grid>
            {renderVideos(false, true)}
          </Grid>
        </>
      )}
      <ModalFileManager
        show={show}
        handleClose={() => setShow(false)}
        fileType={fileType}
        handleUploadSelect={handleUploadSelect}
      />
    </Container>
  );
};

export default Videos;
